import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shul } from '../../types';
import ShulSearch from './ShulSearch';
import ShulCard from './ShulCard';
import { filterAndSortShuls, shulDistance } from '../../utils/shulSearch';
import { LatLng } from '../../utils/geo';

interface ShulListProps {
  shuls: Shul[];
  loading: boolean;
  error: string | null;
  /** The user's location after Near me: nearest first, with distances. */
  origin?: LatLng | null;
}

export default function ShulList({ shuls, loading, error, origin }: ShulListProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const [query, setQuery] = useState('');

  const sorted = useMemo(
    () => filterAndSortShuls(shuls, query, isHebrew, origin),
    [shuls, query, isHebrew, origin]
  );

  return (
    <div className="space-y-3">
      <ShulSearch value={query} onChange={setQuery} />

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-[16px] border border-ink/10 p-3 animate-pulse"
            >
              <div className="shrink-0 w-[88px] h-[88px] rounded-[10px] bg-fog" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/3 rounded bg-fog" />
                <div className="h-3 w-1/2 rounded bg-fog" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <p className="text-center text-sm text-danger-500 py-8">{t('common.error')}</p>
      )}

      {!loading && !error && sorted.length === 0 && (
        <p className="text-center text-sm text-ash py-8">{t('common.noResults')}</p>
      )}

      {!loading && !error && sorted.length > 0 && (
        <div className="space-y-2">
          {sorted.map((shul) => (
            <ShulCard key={shul.id} shul={shul} distance={origin ? shulDistance(shul, origin) : undefined} />
          ))}
        </div>
      )}
    </div>
  );
}
